import React from 'react';
import type { JSX } from 'react';
import { Check, Crosshair, MapPin, X } from 'lucide-react';
import type { ProjectPlan } from '../../types';
import type { Ortsvorschlag } from '../../services/verortung';

/**
 * Ortsvorschlag aus der Kamera im Wizard-Schritt „Wo“ (docs/verortung-kamera-vertrag.md,
 * Orchestrator). Nur ein VORSCHLAG: nichts wird ohne Tipp auf „Übernehmen“ gesetzt.
 *
 * Mit Pin: kleiner Planausschnitt mit Standort, Unsicherheitskreis (GPS-Genauigkeit) und
 * Blickpfeil (Kompass). Der Pin ist der Standort des Geräts, der Pfeil zeigt zum Mangel.
 */

export interface OrtsvorschlagKarteProps {
    vorschlag: Ortsvorschlag;
    /** Plan des Vorschlags (für die Vorschau); null → keine Vorschau. */
    plan: ProjectPlan | null;
    onUebernehmen: () => void;
    /** Übernehmen und danach den Pin von Hand setzen. */
    onAufPlan?: () => void;
    onVerwerfen: () => void;
}

const sicherheitText = (s: number): string => {
    if (s >= 0.75) return 'sicher';
    if (s >= 0.4) return 'wahrscheinlich';
    return 'unsicher';
};

export function OrtsvorschlagKarte({ vorschlag, plan, onUebernehmen, onAufPlan, onVerwerfen }: OrtsvorschlagKarteProps): JSX.Element {
    const s = vorschlag.schaetzung;
    const mitPin = !!(plan && vorschlag.pin);
    return (
        <div className="rounded-2xl border-2 border-sky-300 bg-sky-50 p-4">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-[11px] font-bold uppercase text-sky-700 flex items-center gap-1">
                        <Crosshair className="w-3.5 h-3.5" /> Ort aus dem Foto
                    </p>
                    <p className="mt-1 text-lg font-bold text-slate-900 leading-tight">{vorschlag.text || 'Kein Ort erkannt'}</p>
                    <p className="text-xs text-slate-600 mt-0.5">
                        {vorschlag.quellen.length ? `aus ${vorschlag.quellen.join(' + ')}` : 'keine Sensordaten'}
                        {s && !s.zwischen && s.ebene ? ` · ${sicherheitText(s.sicherheit)}` : ''}
                        {s?.alternative ? ` · oder ${s.alternative.name}` : ''}
                    </p>
                </div>
                <button type="button" onClick={onVerwerfen} aria-label="Vorschlag verwerfen" className="p-1.5 text-slate-400 active:text-slate-600">
                    <X className="w-5 h-5" />
                </button>
            </div>

            {mitPin && plan && vorschlag.pin && (
                <div className="mt-3 relative rounded-lg overflow-hidden border border-slate-300 bg-black">
                    <img src={plan.image} alt={plan.name} className="w-full h-auto block opacity-90" />
                    {vorschlag.pinRadius && (
                        <div
                            className="absolute rounded-full border-2 border-sky-500 bg-sky-400/20 pointer-events-none"
                            style={{
                                left: `${vorschlag.pin.x - vorschlag.pinRadius.rx}%`,
                                top: `${vorschlag.pin.y - vorschlag.pinRadius.ry}%`,
                                width: `${vorschlag.pinRadius.rx * 2}%`,
                                height: `${vorschlag.pinRadius.ry * 2}%`,
                            }}
                        />
                    )}
                    {vorschlag.blickGradPlan !== null && (
                        <svg
                            className="absolute pointer-events-none"
                            style={{ left: `${vorschlag.pin.x}%`, top: `${vorschlag.pin.y}%`, width: 56, height: 56, marginLeft: -28, marginTop: -28, transform: `rotate(${vorschlag.blickGradPlan}deg)` }}
                            viewBox="0 0 56 56"
                            aria-hidden="true"
                        >
                            <line x1="28" y1="28" x2="28" y2="4" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" />
                            <path d="M28 0 L22 10 L34 10 Z" fill="#dc2626" />
                        </svg>
                    )}
                    <MapPin
                        className="absolute w-6 h-6 text-red-600 drop-shadow pointer-events-none"
                        style={{ left: `${vorschlag.pin.x}%`, top: `${vorschlag.pin.y}%`, transform: 'translate(-50%, -100%)' }}
                    />
                    <p className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[11px] px-2 py-1">
                        {plan.name} · Standort{vorschlag.genauigkeitM !== null ? ` ±${Math.round(vorschlag.genauigkeitM)} m` : ''}
                        {vorschlag.blickGradPlan !== null ? ' · Pfeil = Blickrichtung' : ''}
                    </p>
                </div>
            )}

            <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                    type="button"
                    onClick={onUebernehmen}
                    className="py-3 rounded-xl bg-sky-600 text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                >
                    <Check className="w-5 h-5" /> Übernehmen
                </button>
                {onAufPlan ? (
                    <button
                        type="button"
                        onClick={onAufPlan}
                        className="py-3 rounded-xl border-2 border-sky-300 bg-white text-sky-800 font-bold active:scale-95 transition-transform"
                    >
                        Auf Plan ändern
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={onVerwerfen}
                        className="py-3 rounded-xl border-2 border-slate-300 bg-white text-slate-700 font-bold active:scale-95 transition-transform"
                    >
                        Verwerfen
                    </button>
                )}
            </div>
        </div>
    );
}
